import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { getBlogById, createBlog, updateBlog } from '../Config/blogService';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

function BlogEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = id && id !== 'new';
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featuredImage: '',
    author: '',
    tags: [],
    category: '',
    status: 'draft',
    metaTitle: '',
    metaDescription: '',
    isFeatured: false,
    isHidden: false
  });
  const [tagInput, setTagInput] = useState('');
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageResizeDialog, setImageResizeDialog] = useState({ open: false, image: null, width: '', height: '', alignment: 'none' });

  useEffect(() => {
    if (isEditMode) {
      fetchBlog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);


  const fetchBlog = async () => {
    setLoading(true);
    try {
      const response = await getBlogById(id);
      // Handle API response structure: { message, data }
      const blog = response.data || response;
      if (response.message || response.data || response.status === 'success' || response.success) {
        const blogContent = blog.content || '';
        setFormData({
          title: blog.title || '',
          slug: blog.slug || '',
          content: blogContent,
          excerpt: blog.excerpt || '',
          featuredImage: blog.featuredImage || '',
          author: blog.author || '',
          tags: blog.tags || [],
          category: blog.category || '',
          status: blog.status || 'draft',
          metaTitle: blog.metaTitle || '',
          metaDescription: blog.metaDescription || '',
          isFeatured: blog.isFeatured || false,
          isHidden: blog.isHidden || false
        });
        // Convert HTML to Draft.js EditorState
        if (blogContent) {
          try {
            const contentBlock = htmlToDraft(blogContent);
            if (contentBlock && contentBlock.contentBlocks) {
              const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
              const editorState = EditorState.createWithContent(contentState);
              setEditorState(editorState);
              
              // Apply image alignment styles after editor is rendered
              setTimeout(() => {
                const editorElement = document.querySelector('.rdw-editor-main');
                if (editorElement) {
                  const images = editorElement.querySelectorAll('img');
                  images.forEach(img => {
                    const align = img.getAttribute('align') || img.style.float || '';
                    if (align === 'left' || img.style.float === 'left') {
                      img.style.float = 'left';
                      img.style.margin = '0 15px 15px 0';
                      img.className = img.className.replace(/image-align-\w+/g, '') + ' image-align-left';
                    } else if (align === 'right' || img.style.float === 'right') {
                      img.style.float = 'right';
                      img.style.margin = '0 0 15px 15px';
                      img.className = img.className.replace(/image-align-\w+/g, '') + ' image-align-right';
                    } else if (align === 'center' || (img.style.display === 'block' && img.style.margin === '0 auto')) {
                      img.style.display = 'block';
                      img.style.margin = '0 auto 15px auto';
                      img.className = img.className.replace(/image-align-\w+/g, '') + ' image-align-center';
                    }
                  });
                }
              }, 100);
            } else {
              // If conversion fails, create empty editor state
              setEditorState(EditorState.createEmpty());
            }
          } catch (error) {
            console.error('Error converting HTML to Draft.js:', error);
            setEditorState(EditorState.createEmpty());
          }
        } else {
          setEditorState(EditorState.createEmpty());
        }
      } else {
        toast.error(response.message || 'Failed to fetch blog');
        navigate('/blogs');
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      toast.error('An error occurred while fetching blog');
      navigate('/blogs');
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-generate slug from title
    if (name === 'title' && !isEditMode) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  // Function to clean HTML and remove extra spacing between paragraphs
  const cleanHtmlContent = (html) => {
    if (!html) return '';
    
    let cleanedHtml = html;
    
    // Remove literal \n characters (newline characters) from HTML
    cleanedHtml = cleanedHtml.replace(/\\n/g, '');
    cleanedHtml = cleanedHtml.replace(/\n/g, '');
    
    // Remove empty paragraphs (but keep paragraphs with only whitespace/newlines if they have content)
    cleanedHtml = cleanedHtml.replace(/<p>\s*<\/p>/g, '');
    
    // Remove extra line breaks and whitespace between paragraphs
    cleanedHtml = cleanedHtml.replace(/<\/p>\s*\n\s*<p>/g, '</p><p>');
    cleanedHtml = cleanedHtml.replace(/<\/p>\s+<p>/g, '</p><p>');
    
    // Remove multiple consecutive <br> tags (more than 2)
    cleanedHtml = cleanedHtml.replace(/(<br\s*\/?>){3,}/gi, '<br><br>');
    
    // Remove <br> tags at the start or end of paragraphs
    cleanedHtml = cleanedHtml.replace(/<p>\s*<br\s*\/?>\s*/gi, '<p>');
    cleanedHtml = cleanedHtml.replace(/\s*<br\s*\/?>\s*<\/p>/gi, '</p>');
    
    // Clean up any remaining extra whitespace between tags
    cleanedHtml = cleanedHtml.replace(/>\s{2,}</g, '><');
    
    // Normalize spacing - ensure single space between closing and opening tags
    cleanedHtml = cleanedHtml.replace(/<\/p><p>/g, '</p><p>');
    
    return cleanedHtml.trim();
  };

  const onEditorStateChange = (newEditorState) => {
    setEditorState(newEditorState);
    // Convert Draft.js content to HTML
    const rawHtml = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
    // Clean the HTML to remove extra spacing
    const htmlContent = cleanHtmlContent(rawHtml);
    setFormData(prev => ({ ...prev, content: htmlContent }));
  };

  // Handle image resize
  useEffect(() => {
    const handleImageClick = (e) => {
      if (e.target.tagName === 'IMG' && e.target.closest('.rdw-editor-main')) {
        e.preventDefault();
        const img = e.target;
        const currentWidth = img.style.width || img.width || img.getAttribute('width') || 'auto';
        const currentHeight = img.style.height || img.height || img.getAttribute('height') || 'auto';
        
        // Get current alignment
        let currentAlignment = 'none';
        const floatStyle = window.getComputedStyle(img).float;
        const textAlign = img.style.textAlign || img.getAttribute('align') || '';
        if (floatStyle === 'left' || textAlign === 'left') {
          currentAlignment = 'left';
        } else if (floatStyle === 'right' || textAlign === 'right') {
          currentAlignment = 'right';
        } else if ((img.style.display === 'block' && img.style.margin === '0 auto') || textAlign === 'center') {
          currentAlignment = 'center';
        }
        
        setImageResizeDialog({
          open: true,
          image: img,
          width: currentWidth === 'auto' ? '' : currentWidth.toString().replace('px', '').replace('%', ''),
          height: currentHeight === 'auto' ? '' : currentHeight.toString().replace('px', '').replace('%', ''),
          alignment: currentAlignment
        });
      }
    };

    const editorElement = document.querySelector('.rdw-editor-main');
    if (editorElement) {
      editorElement.addEventListener('click', handleImageClick);
      return () => {
        editorElement.removeEventListener('click', handleImageClick);
      };
    }
  }, [editorState]);

  const handleImageResize = () => {
    if (imageResizeDialog.image) {
      const img = imageResizeDialog.image;
      
      // Handle width
      if (imageResizeDialog.width) {
        const widthValue = imageResizeDialog.width.includes('%') || imageResizeDialog.width.includes('px') 
          ? imageResizeDialog.width 
          : `${imageResizeDialog.width}px`;
        img.style.width = widthValue;
        img.setAttribute('width', widthValue);
      } else {
        img.style.width = 'auto';
        img.removeAttribute('width');
      }
      
      // Handle height
      if (imageResizeDialog.height) {
        const heightValue = imageResizeDialog.height.includes('%') || imageResizeDialog.height.includes('px')
          ? imageResizeDialog.height
          : `${imageResizeDialog.height}px`;
        img.style.height = heightValue;
        img.setAttribute('height', heightValue);
      } else {
        img.style.height = 'auto';
        img.removeAttribute('height');
      }
      
      // Handle alignment
      img.style.float = '';
      img.style.display = '';
      img.style.margin = '';
      img.removeAttribute('align');
      
      if (imageResizeDialog.alignment === 'left') {
        img.style.float = 'left';
        img.style.margin = '0 15px 15px 0';
        img.setAttribute('align', 'left');
      } else if (imageResizeDialog.alignment === 'right') {
        img.style.float = 'right';
        img.style.margin = '0 0 15px 15px';
        img.setAttribute('align', 'right');
      } else if (imageResizeDialog.alignment === 'center') {
        img.style.display = 'block';
        img.style.margin = '0 auto 15px auto';
        img.setAttribute('align', 'center');
      }
      
      // Add class for styling
      img.className = img.className.replace(/image-align-\w+/g, '');
      if (imageResizeDialog.alignment !== 'none') {
        img.className += ` image-align-${imageResizeDialog.alignment}`;
      }
      
      // Update editor state with the modified content
      setTimeout(() => {
        const editorElement = document.querySelector('.rdw-editor-main');
        if (editorElement) {
          const htmlContent = editorElement.innerHTML;
          const cleanedContent = cleanHtmlContent(htmlContent);
          setFormData(prev => ({ ...prev, content: cleanedContent }));
        }
      }, 100);
    }
    setImageResizeDialog({ open: false, image: null, width: '', height: '', alignment: 'none' });
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()]
        }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Image upload function for Supabase
  const uploadImageToSupabase = async (file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      'https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload',
      {
        method: 'POST',
        headers: {
          'X-Client': 'ADMIN',
          'Authorization': `Bearer ${token}`
        },
        body: formData
      }
    );

    if (!response.ok) {
      throw new Error('Failed to upload image');
    }

    const uploadData = await response.json();
    return uploadData[0]; // Return the URL
  };

  // Handle image upload in editor with size options
  const handleEditorImageUpload = async (file) => {
    setUploadingImage(true);
    try {
      const imageUrl = await uploadImageToSupabase(file);
      toast.success('Image uploaded successfully!');
      // Return image with default size attributes that can be resized
      return { data: { link: imageUrl, width: 'auto', height: 'auto' } };
    } catch (error) {
      toast.error('Failed to upload image');
      return { data: { link: '' } };
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle featured image upload
  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const imageUrl = await uploadImageToSupabase(file);
      setFormData(prev => ({ ...prev, featuredImage: imageUrl }));
      toast.success('Featured image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload featured image');
    } finally {
      setUploadingImage(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Get the latest content from editor and clean it one more time before saving
    const currentContent = editorState ? 
      cleanHtmlContent(draftToHtml(convertToRaw(editorState.getCurrentContent()))) : 
      cleanHtmlContent(formData.content);
    
    const dataToSubmit = { ...formData, content: currentContent };

    try {
      let response;
      if (isEditMode) {
        response = await updateBlog(id, dataToSubmit);
      } else {
        response = await createBlog(dataToSubmit);
      }

      if (response.message || response.status === 'success' || response.success) {
        toast.success(response.message || `Blog ${isEditMode ? 'updated' : 'created'} successfully`);
        navigate('/blogs');
      } else {
        toast.error(response.message || `Failed to ${isEditMode ? 'update' : 'create'} blog`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} blog:`, error);
      toast.error(`An error occurred while ${isEditMode ? 'updating' : 'creating'} blog`);
    }
    setSaving(false);
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Topbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">
            {isEditMode ? 'Edit Blog' : 'Create New Blog'}
          </h1>
          <button
            onClick={() => navigate('/blogs')}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
          >
            Back to Blogs
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            {/* Slug */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            {/* Author */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Author <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Featured Image */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured Image
              </label>
              <div className="space-y-3">
                {/* Upload Button */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFeaturedImageUpload}
                    disabled={uploadingImage}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                  />
                  {uploadingImage && (
                    <p className="mt-1 text-sm text-gray-500">Uploading...</p>
                  )}
                </div>
                
                {/* OR Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">OR</span>
                  </div>
                </div>

                {/* URL Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter Image URL
                  </label>
                  <input
                    type="url"
                    name="featuredImage"
                    value={formData.featuredImage}
                    onChange={handleInputChange}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                {/* Preview */}
                {formData.featuredImage && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                    <img
                      src={formData.featuredImage}
                      alt="Featured"
                      className="max-w-xs rounded border border-gray-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Excerpt */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Excerpt
              </label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                rows="6"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 resize-y"
                placeholder="Brief description of the blog post"
              />
            </div>

            {/* Tags */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleAddTag}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Type a tag and press Enter"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content <span className="text-red-500">*</span>
              </label>
              <div className="border rounded-md bg-white relative">
                <style>{`
                  .rdw-editor-toolbar {
                    position: sticky !important;
                    top: 0 !important;
                    z-index: 10 !important;
                    background: white !important;
                    border-bottom: 1px solid #e5e7eb !important;
                    padding: 8px !important;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
                  }
                  .rdw-editor-wrapper .rdw-editor-main img {
                    max-width: 100% !important;
                    height: auto !important;
                    cursor: pointer !important;
                    display: inline-block !important;
                    position: relative !important;
                    vertical-align: middle !important;
                  }
                  .rdw-editor-wrapper .rdw-editor-main img:hover {
                    outline: 2px dashed #3b82f6 !important;
                    outline-offset: 2px !important;
                  }
                  .rdw-editor-wrapper .rdw-editor-main img.image-align-left {
                    float: left !important;
                    margin: 0 15px 15px 0 !important;
                    max-width: 50% !important;
                  }
                  .rdw-editor-wrapper .rdw-editor-main img.image-align-right {
                    float: right !important;
                    margin: 0 0 15px 15px !important;
                    max-width: 50% !important;
                  }
                  .rdw-editor-wrapper .rdw-editor-main img.image-align-center {
                    display: block !important;
                    margin: 0 auto 15px auto !important;
                  }
                  .rdw-editor-wrapper .rdw-editor-main p {
                    overflow: hidden !important;
                  }
                  .rdw-image-modal {
                    z-index: 100 !important;
                  }
                  .rdw-image-modal input[type="text"] {
                    margin-bottom: 10px !important;
                  }
                  .rdw-image-modal .rdw-image-size-input {
                    display: flex !important;
                    gap: 10px !important;
                    margin-top: 10px !important;
                  }
                  .rdw-image-modal .rdw-image-size-input input {
                    width: 100px !important;
                    padding: 5px !important;
                    border: 1px solid #ccc !important;
                    border-radius: 4px !important;
                  }
                `}</style>
                <Editor
                  editorState={editorState}
                  onEditorStateChange={onEditorStateChange}
                  toolbar={{
                    options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
                    inline: {
                      inDropdown: false,
                      className: undefined,
                      component: undefined,
                      dropdownClassName: undefined,
                      options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
                    },
                    blockType: {
                      inDropdown: true,
                      options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
                      className: undefined,
                      component: undefined,
                      dropdownClassName: undefined,
                    },
                    fontSize: {
                      options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                      className: undefined,
                      component: undefined,
                      dropdownClassName: undefined,
                    },
                    fontFamily: {
                      options: ['Arial', 'Georgia', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana'],
                      className: undefined,
                      component: undefined,
                      dropdownClassName: undefined,
                    },
                    list: {
                      inDropdown: false,
                      className: undefined,
                      component: undefined,
                      dropdownClassName: undefined,
                      options: ['unordered', 'ordered', 'indent', 'outdent'],
                    },
                    textAlign: {
                      inDropdown: false,
                      className: undefined,
                      component: undefined,
                      dropdownClassName: undefined,
                      options: ['left', 'center', 'right', 'justify'],
                    },
                    colorPicker: {
                      className: undefined,
                      component: undefined,
                      popupClassName: undefined,
                      colors: ['rgb(97, 189, 109)', 'rgb(26, 188, 156)', 'rgb(84, 172, 210)', 'rgb(44, 130, 201)',
                        'rgb(147, 101, 184)', 'rgb(71, 85, 119)', 'rgb(204, 204, 204)', 'rgb(65, 168, 95)',
                        'rgb(0, 168, 133)', 'rgb(61, 142, 185)', 'rgb(41, 105, 176)', 'rgb(85, 57, 130)',
                        'rgb(40, 50, 78)', 'rgb(0, 0, 0)', 'rgb(247, 218, 100)', 'rgb(251, 160, 38)',
                        'rgb(235, 107, 86)', 'rgb(226, 80, 65)', 'rgb(163, 143, 132)', 'rgb(239, 239, 239)',
                        'rgb(255, 255, 255)', 'rgb(250, 197, 28)', 'rgb(243, 121, 52)', 'rgb(209, 72, 65)',
                        'rgb(184, 49, 47)', 'rgb(124, 112, 107)', 'rgb(209, 213, 216)', 'rgb(81, 90, 94)'],
                    },
                    link: {
                      inDropdown: false,
                      className: undefined,
                      component: undefined,
                      popupClassName: undefined,
                      dropdownClassName: undefined,
                      showOpenOptionOnHover: true,
                      defaultTargetOption: '_self',
                      options: ['link', 'unlink'],
                    },
                    emoji: {
                      className: undefined,
                      component: undefined,
                      popupClassName: undefined,
                      emojis: [
                        '😀', '😁', '😂', '😃', '😉', '😋', '😎', '😍', '😗', '🤗', '🤔', '😣', '😫', '😴', '😌', '🤓',
                        '😛', '😜', '😠', '😇', '😷', '😈', '👻', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '🙈', '🙉', '🙊',
                        '👼', '👮', '🕵', '💂', '👳', '🎅', '👸', '👰', '👲', '🙍', '🙇', '🚶', '🏃', '💃', '⛷', '🏂', '🏌',
                        '🏄', '🚣', '🏊', '⛹', '🏋', '🚴', '👫', '💪', '👈', '👉', '👉', '👆', '🖕', '👇', '🖖', '🤘', '🖐',
                        '👌', '👍', '👎', '✊', '👊', '👏', '🙌', '🙏', '🐵', '🐶', '🐇', '🐥', '🐸', '🐌', '🐛', '🐜', '🐝',
                        '🐞', '🐾', '💐', '🌸', '🌷', '🍀', '🌹', '🌻', '🌺', '🍁', '🍃', '🍂', '🌿', '🌾', '🍄', '🌵', '🌴',
                        '🌲', '🌳', '🌰', '🌱', '🌼', '🌐', '🌞', '🌝', '🌚', '🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘',
                        '🌜', '🌛', '🌙', '💫', '⭐', '🌟', '✨', '⚡', '☄', '💥', '🔥', '🌪', '🌈', '☀', '🌤', '⛅', '☁', '⛈',
                        '🌦', '🌧', '⛈', '🌩', '🌨', '❄', '⛄', '☂', '💧', '💦', '☔', '☕', '🍵', '🍶', '🍼', '🍺', '🍻',
                        '🍸', '🍹', '🍷', '🍴', '🍕', '🍔', '🍟', '🍗', '🍖', '🍝', '🍛', '🍤', '🍱', '🍣', '🍥', '🍙', '🍘',
                        '🍚', '🍜', '🍲', '🍢', '🍡', '🍳', '🍞', '🍩', '🍮', '🍦', '🍨', '🍧', '🎂', '🍰', '🍪', '🍫', '🍬',
                        '🍭', '🍯', '🍎', '🍏', '🍊', '🍋', '🍒', '🍇', '🍉', '🍓', '🍑', '🍈', '🍌', '🍐', '🍍', '🍠', '🍆', '🍅', '🌽',
                      ],
                    },
                    image: {
                      className: undefined,
                      component: undefined,
                      popupClassName: undefined,
                      urlEnabled: true,
                      uploadEnabled: true,
                      alignmentEnabled: true,
                      uploadCallback: handleEditorImageUpload,
                      previewImage: false,
                      inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg,image/webp',
                      alt: { present: false, mandatory: false },
                      defaultSize: {
                        height: 'auto',
                        width: 'auto',
                      },
                    },
                  }}
                  editorClassName="editor-class"
                  wrapperClassName="wrapper-class"
                  toolbarClassName="toolbar-class"
                  placeholder="Start writing your blog content here..."
                />
              </div>
            </div>

            {/* SEO Fields */}
            <div className="md:col-span-2 border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold mb-4">SEO Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meta Title
                  </label>
                  <input
                    type="text"
                    name="metaTitle"
                    value={formData.metaTitle}
                    onChange={handleInputChange}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meta Description
                  </label>
                  <textarea
                    name="metaDescription"
                    value={formData.metaDescription}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="md:col-span-2 border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold mb-4">Options</h3>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={formData.isFeatured}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Featured Blog</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isHidden"
                    checked={formData.isHidden}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Hide from public</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/blogs')}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-400"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? 'Saving...' : (isEditMode ? 'Update Blog' : 'Create Blog')}
            </button>
          </div>
        </form>

        <ToastContainer />

        {/* Image Resize Dialog */}
        {imageResizeDialog.open && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[120]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold mb-4">Resize & Align Image</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alignment
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    <button
                      type="button"
                      onClick={() => setImageResizeDialog(prev => ({ ...prev, alignment: 'left' }))}
                      className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                        imageResizeDialog.alignment === 'left'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      title="Left - Image on left, text on right"
                    >
                      Left
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageResizeDialog(prev => ({ ...prev, alignment: 'center' }))}
                      className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                        imageResizeDialog.alignment === 'center'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      title="Center - Image centered"
                    >
                      Center
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageResizeDialog(prev => ({ ...prev, alignment: 'right' }))}
                      className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                        imageResizeDialog.alignment === 'right'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      title="Right - Image on right, text on left"
                    >
                      Right
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageResizeDialog(prev => ({ ...prev, alignment: 'none' }))}
                      className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                        imageResizeDialog.alignment === 'none'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      title="None - Default inline"
                    >
                      None
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {imageResizeDialog.alignment === 'left' && 'Image will float left with text wrapping on the right'}
                    {imageResizeDialog.alignment === 'right' && 'Image will float right with text wrapping on the left'}
                    {imageResizeDialog.alignment === 'center' && 'Image will be centered'}
                    {imageResizeDialog.alignment === 'none' && 'Image will be inline with text'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Width (px or %)
                  </label>
                  <input
                    type="text"
                    value={imageResizeDialog.width}
                    onChange={(e) => setImageResizeDialog(prev => ({ ...prev, width: e.target.value }))}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="e.g., 300px or 50%"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    For side-by-side layouts, use 30-50% width
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Height (px or %)
                  </label>
                  <input
                    type="text"
                    value={imageResizeDialog.height}
                    onChange={(e) => setImageResizeDialog(prev => ({ ...prev, height: e.target.value }))}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="e.g., 300px or auto"
                  />
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setImageResizeDialog({ open: false, image: null, width: '', height: '', alignment: 'none' })}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleImageResize}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BlogEdit;

